import { BeaconRenderProps, Step, TooltipRenderProps } from "react-joyride";
import { useTranslation, LangKey } from "@vixi/localization";
import { CourseTabData, hasContent } from "@vixi/types";
import { STEP_TYPE } from "@vixi/types";
import { ComponentProps, forwardRef } from "react";
import { courseTabsClasses } from "@/classes/courseTabsClasses";
import { editableBoxClasses } from "@/classes/editableBoxClasses";
import { conversationPaginationClasses } from "@/classes/conversationPaginationClasses";
import { mcAnswerClasses } from "./CourseStepContent/QuestionStep/MultipleChoiceStep";
import { cn } from "@/lib/utils";
import { Button, ButtonProps } from "../base/button";
import { Img } from "../base/img";

export interface TourStep extends Step {
  content: LangKey;
  image?: string;
  data?: {
    initialTabValues: (number | ((tab: CourseTabData) => boolean))[];
  };
}

// Constants
export const tour_steps: TourStep[] = [
  {
    target: `.${courseTabsClasses.depth(0)} .${courseTabsClasses.nav}`,
    content: "Course.Tour.Chapters",
    image: "/images/screens/chapterscreen.png",
    placement: "right-start",
    placementBeacon: "bottom",
  },
  {
    target: `.${courseTabsClasses.depth(1)} .${courseTabsClasses.nav}`,
    content: "Course.Tour.Lessons",
    image: "/images/screens/lessonscreen.png",
    placement: "right-start",
    placementBeacon: "bottom",
  },
  {
    target: `.${courseTabsClasses.depth(1)} .${courseTabsClasses.tabItemArrowDir("end")}`,
    content: "Course.Tour.LessonTitles",
    placement: "right-start",
    placementBeacon: "bottom",
    spotlightPadding: 0,
  },
  {
    target: `.${courseTabsClasses.depth(2)} .${courseTabsClasses.nav}`,
    content: "Course.Tour.Steps",
    placement: "left",
    placementBeacon: "bottom",
  },
  {
    target: `.${courseTabsClasses.depth(2)} .${courseTabsClasses.tabList}`,
    content: "Course.Tour.StepsComposedOf",
    placement: "left",
    placementBeacon: "bottom",
  },
  {
    target: `.${courseTabsClasses.depth(1)} .${courseTabsClasses.addItemButton}`,
    content: "Course.Tour.AddButtons",
    placement: "top",
    placementBeacon: "bottom",
  },
  {
    target: `.${courseTabsClasses.depth(1)} .${courseTabsClasses.modificationButtonsRoot}`,
    content: "Course.Tour.ModificationButtons",
    placement: "top",
    placementBeacon: "bottom",
    spotlightPadding: 0,
    data: {
      initialTabValues: [0, 0, 0],
    },
  },
  {
    target: `.${courseTabsClasses.contentType("conversation")}`,
    content: "Course.Tour.Conversation",
    image: "/images/screens/convscreen1.png",
    placement: "left",
    placementBeacon: "bottom",
    data: {
      initialTabValues: [
        0,
        0,
        (tab) => hasContent(tab) && tab.content.type === STEP_TYPE.CONVERSATION,
      ],
    },
  },
  {
    target: `.${courseTabsClasses.contentType("conversation")} .${editableBoxClasses.root}`,
    content: "Course.Tour.ConversationTextBox",
    placement: "left",
    placementBeacon: "bottom",
  },
  {
    target: `.${courseTabsClasses.contentType("conversation")} .${conversationPaginationClasses.navButtonDir("end")}`,
    content: "Course.Tour.ConversationNavArrows",
    placement: "top-end",
    spotlightPadding: 0,
  },
  {
    // MCQ example - targeting the second tab item in the steps section
    target: `.${courseTabsClasses.depth(2)} .${courseTabsClasses.tabList} .TabItem-root:nth-child(2)`,
    content: "Course.Tour.Mcq",
    image: "/images/screens/convscreen2.png",
    placement: "left",
    placementBeacon: "bottom",
    spotlightPadding: 0,
    data: {
      initialTabValues: [0, 0, 1],
    },
  },
  {
    target: `.${mcAnswerClasses.checkMark}`,
    content: "Course.Tour.McqCheckMark",
    placement: "right",
    placementBeacon: "bottom",
  },
];

// Styled Components
const TourTooltipRoot = (props: ComponentProps<"div">) => (
  <div
    {...props}
    className={cn(
      "border-primary bg-paper relative flex max-w-85 min-w-65 flex-col items-center rounded border-2 p-5 shadow-xs",
      props.className,
    )}
  />
);

const TourTooltipText = (props: ComponentProps<"p">) => (
  <p {...props} className={cn("fs-16 font-semibold text-black")} />
);

const TourTooltipFooter = (props: ComponentProps<"div">) => (
  <div {...props} className={cn("mt-6.25 flex w-full justify-end gap-2.75")} />
);

const TourTooltipButton = (props: ButtonProps) => (
  <Button {...props} className={cn("fs-14 min-w-20.75 pt-1 pb-1.25", props.className)} />
);

export function TourTooltip({
  step,
  tooltipProps,
  primaryProps,
  continuous,
  skipProps,
}: TooltipRenderProps) {
  const { t } = useTranslation();
  const tourStep = step as TourStep;
  const imageSrc = (tourStep && (tourStep as TourStep).image) || undefined;

  return (
    <TourTooltipRoot {...tooltipProps}>
      {imageSrc ? (
        <>
          <TourTooltipText className="mb-12">
            {typeof step.content === "string" ? t(step.content as LangKey) : step.content}
          </TourTooltipText>
          <Img
            contain
            src={imageSrc}
            alt=""
            className="mb-4 h-80 w-auto"
          />
        </>
      ) : (
        <TourTooltipText>
          {typeof step.content === "string" ? t(step.content as LangKey) : step.content}
        </TourTooltipText>
      )}

      <TourTooltipFooter>
        {continuous && (
          <>
            <TourTooltipButton variant="outlined" size="pill" {...skipProps} className="me-0.5">
              {skipProps.title}
            </TourTooltipButton>
            <TourTooltipButton variant="contained" size="pill" {...primaryProps}>
              {primaryProps.title}
            </TourTooltipButton>
          </>
        )}
      </TourTooltipFooter>
    </TourTooltipRoot>
  );
}

export const NoTourBeacon = forwardRef<HTMLDivElement, BeaconRenderProps>((_props, ref) => {
  return <div ref={ref}></div>;
});
